import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import AddIcon from '@mui/icons-material/Add';
import { fetchData } from '../../helpers/apiUtils';
import { ClientTemplateConfig } from '../../helpers/webinarMailingData';
import TemplateEditor from './TemplateEditor';

interface TemplateFile {
  filename: string;
  label: string;
}

const ClientConfigTab: React.FC = () => {
  const [clients, setClients] = useState<ClientTemplateConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit dialog state (for template assignment)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientTemplateConfig | null>(null);
  const [templateFiles, setTemplateFiles] = useState<TemplateFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Template editor state
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [editingTemplateFile, setEditingTemplateFile] = useState<string | null>(null);

  const loadClients = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchData<ClientTemplateConfig[]>(
        '/api/webinar-mailings/client-templates',
      );
      if (response.success && response.data) {
        setClients(response.data);
      } else {
        setError('Fehler beim Laden der Kunden-Konfiguration');
      }
    } catch (err) {
      setError('Fehler beim Laden der Kunden-Konfiguration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateFiles = async () => {
    try {
      const response = await fetchData<{ files: string[] }>('/api/webinar-mailings/template-files');
      if (response.success && response.data?.files) {
        setTemplateFiles(
          response.data.files.map((f) => ({
            filename: f,
            label: f.replace('.html', '').replace(/-/g, ' '),
          })),
        );
      }
    } catch (err) {
      console.error('Error loading template files:', err);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleEditClick = async (client: ClientTemplateConfig) => {
    // FT (Iterable) cannot be edited
    if (client.client_code === 'ft') {
      return;
    }

    setEditingClient(client);
    setSelectedFile(client.template_file || '');
    setEditDialogOpen(true);
    setTemplateFiles([]);

    // Load available template files from server
    setLoadingFiles(true);
    await loadTemplateFiles();
    setLoadingFiles(false);
  };

  const handleEditTemplateHtml = (filename: string) => {
    setEditingTemplateFile(filename);
    setTemplateEditorOpen(true);
  };

  const handleCreateNewTemplate = () => {
    setEditingTemplateFile(null);
    setTemplateEditorOpen(true);
  };

  const handleTemplateSaved = () => {
    setSuccess('Template gespeichert');
    setTimeout(() => setSuccess(null), 3000);
    loadTemplateFiles();
  };

  const handleSave = async () => {
    if (!editingClient) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetchData<{ message?: string }>(
        `/api/webinar-mailings/client-templates/${editingClient.client_code}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            template_file: selectedFile || null,
            updated_by: 'dashboard-user', // TODO: Get from auth context
          }),
        },
      );

      if (response.success) {
        setSuccess(response.data?.message || 'Template gespeichert');
        setEditDialogOpen(false);
        loadClients();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Fehler beim Speichern');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getEspChip = (espType: string) => {
    const isMailchimp = espType === 'mailchimp';
    return (
      <Chip
        size="small"
        label={isMailchimp ? 'Mailchimp' : 'Iterable'}
        sx={{
          backgroundColor: isMailchimp ? '#ec4899' : '#6366f1',
          color: 'white',
          fontWeight: 500,
        }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Kunden-Konfiguration</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateNewTemplate}>
          Neues Template
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Hier können Sie für jeden Mailchimp-Kunden eine Template-Datei festlegen. Das Template wird
        als Wrapper um den E-Mail-Inhalt verwendet (der Inhalt ersetzt den
        &#123;&#123;CONTENT&#125;&#125; Platzhalter).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Kunde
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                ESP
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Template-Datei
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Zuletzt geändert
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="center"
              >
                Aktion
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => {
              const isIterable = client.esp_type === 'iterable';
              return (
                <TableRow
                  key={client.client_code}
                  sx={{
                    backgroundColor: isIterable ? '#f5f5f5' : 'inherit',
                    opacity: isIterable ? 0.7 : 1,
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={500}>{client.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {client.client_code.toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>{getEspChip(client.esp_type)}</TableCell>
                  <TableCell>
                    {client.template_file ? (
                      <Chip
                        size="small"
                        label={client.template_file}
                        variant={isIterable ? 'outlined' : 'filled'}
                        color={isIterable ? 'default' : 'primary'}
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Kein Template
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {client.updated_at ? (
                      <>
                        <Typography variant="body2">
                          {new Date(client.updated_at).toLocaleDateString('de-DE')}
                        </Typography>
                        {client.updated_by && (
                          <Typography variant="caption" color="text.secondary">
                            von {client.updated_by}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isIterable ? (
                      <Typography variant="caption" color="text.secondary">
                        Fest konfiguriert
                      </Typography>
                    ) : (
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(client)}
                          title="Template zuweisen"
                        >
                          <EditIcon />
                        </IconButton>
                        {client.template_file && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditTemplateHtml(client.template_file!)}
                            title="Template HTML bearbeiten"
                            color="primary"
                          >
                            <CodeIcon />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog (Template Assignment) */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Template für {editingClient?.client_name} bearbeiten
          {!loadingFiles && templateFiles.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({templateFiles.length} Dateien verfügbar)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {loadingFiles ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
                <Typography ml={2}>Template-Dateien werden geladen...</Typography>
              </Box>
            ) : (
              <Autocomplete
                options={[{ filename: '', label: 'Kein Template (Raw HTML)' }, ...templateFiles]}
                getOptionLabel={(option) => option.filename || option.label}
                value={
                  templateFiles.find((t) => t.filename === selectedFile) ||
                  (selectedFile === '' ? { filename: '', label: 'Kein Template (Raw HTML)' } : null)
                }
                onChange={(_, newValue) => setSelectedFile(newValue?.filename || '')}
                isOptionEqualToValue={(option, value) => option.filename === value.filename}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Template-Datei auswählen"
                    placeholder="Datei auswählen..."
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.filename || 'no-template'}>
                    <Box>
                      <Typography sx={{ fontFamily: option.filename ? 'monospace' : 'inherit' }}>
                        {option.filename || option.label}
                      </Typography>
                    </Box>
                  </li>
                )}
                fullWidth
              />
            )}

            {templateFiles.length === 0 && !loadingFiles && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Keine Template-Dateien auf dem Server gefunden. Templates werden im Verzeichnis{' '}
                <code>node-app/templates/</code> erwartet.
              </Alert>
            )}

            {selectedFile && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Die Template-Datei muss einen <code>&#123;&#123;CONTENT&#125;&#125;</code>{' '}
                Platzhalter enthalten, an dessen Stelle der E-Mail-Inhalt eingefügt wird.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || loadingFiles}>
            {saving ? <CircularProgress size={20} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Editor Modal */}
      <TemplateEditor
        open={templateEditorOpen}
        filename={editingTemplateFile}
        onClose={() => setTemplateEditorOpen(false)}
        onSaved={handleTemplateSaved}
      />
    </Box>
  );
};

export default ClientConfigTab;
